// ============================================================================
// Módulo CALIFICACIONES — estadísticas de la experiencia de los comensales.
// Solo visible para superadmin (gerente_administrativo) y desarrollador
// (ROLE_MODULES) y si el flag ratings_module_enabled está encendido.
//
// Los datos nacen del QR post-pago: cada venta pagada genera un token público
// y el comensal responde desde su celular (/f/calificar/<token>). Acá se ven
// los promedios por mesero, la tasa de respuesta y las respuestas recientes
// con el contexto completo de la venta (qué pidió, apertura/cierre, total).
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { Star, MessageSquareText } from 'lucide-react';
import { useBranch } from '../context/branch-context';
import { listCalificaciones, getCalificacionesStats, type Calificacion, type CalificacionesStats } from '../../api/calificaciones';
import { ModuleHeader } from './ui/module-header';
import { formatMoney } from '@/lib/moneda';
import { useTextos } from '../context/regional-context';
import { textosCalificaciones } from '../../i18n/modulos/calificaciones';

// "YYYY-MM-01" del mes actual (para el filtro por defecto).
function inicioMes(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function fmtFechaHora(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('es-CO', { timeZone: 'America/Bogota',  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// Estrellas read-only compactas.
function Stars({ n }: { n: number | null }) {
  const tx = useTextos(textosCalificaciones);
  if (n == null) return <span style={{ color: 'var(--muted-2, #94a3b8)' }}>—</span>;
  return (
    <span className="inline-flex items-center gap-0.5" title={tx('nDe5', { n })}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className="w-3.5 h-3.5"
          style={{ color: i <= n ? '#E6A100' : 'var(--line-2)', fill: i <= n ? '#E6A100' : 'transparent' }} />
      ))}
    </span>
  );
}

function Prom({ v }: { v: number | null }) {
  if (v == null) return <span style={{ color: 'var(--muted-2, #94a3b8)' }}>—</span>;
  const color = v >= 4.2 ? '#047857' : v >= 3.2 ? '#92400e' : '#b91c1c';
  return <b className="tabular-nums" style={{ color }}>{v.toFixed(2)}</b>;
}

export function Ratings() {
  const { currentBranch, currentBranchId } = useBranch();
  const tx = useTextos(textosCalificaciones);
  const [periodo, setPeriodo] = useState<'mes' | 'todos'>('mes');
  const [stats, setStats] = useState<CalificacionesStats | null>(null);
  const [lista, setLista] = useState<Calificacion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentBranchId) return;
    setCargando(true);
    setLoadError(null);
    const q = { sede_id: currentBranchId, desde: periodo === 'mes' ? inicioMes() : undefined };
    Promise.all([getCalificacionesStats(q), listCalificaciones({ ...q, limit: 300 })])
      .then(([s, l]) => { setStats(s); setLista(l); })
      .catch((err: any) => {
        const status = err?.status ? ` (HTTP ${err.status})` : '';
        setLoadError(
          `${err?.message || tx('errorCargando')}${status}.` +
          (err?.status === 404 ? tx('reiniciasteApi') : '')
        );
      })
      .finally(() => setCargando(false));
  }, [currentBranchId, periodo]);

  const respondidas = useMemo(() => lista.filter(c => c.estado === 'completada'), [lista]);
  const tasa = stats && stats.global.emitidas > 0
    ? Math.round((stats.global.respondidas / stats.global.emitidas) * 100)
    : 0;

  return (
    <div className="pb-32 max-w-screen-2xl mx-auto">
      <ModuleHeader
        title={tx('titulo')}
        icon={Star}
        stats={[
          { n: stats?.global.respondidas ?? 0, label: tx('respondidas'), tone: 'blue' },
          { n: `${tasa}%`, label: tx('tasaRespuesta') },
          { n: stats?.global.atencion?.toFixed(2) ?? '—', label: tx('atencion'), tone: 'ok' },
          { n: stats?.global.comida?.toFixed(2) ?? '—', label: tx('comida'), tone: 'ok' },
          { n: stats?.global.recomendacion?.toFixed(2) ?? '—', label: tx('recomendacion'), tone: 'ok' },
        ]}
        secondaryFilters={{
          value: periodo,
          onChange: (v) => setPeriodo(v as 'mes' | 'todos'),
          options: [
            { value: 'mes', label: tx('mesEnCurso') },
            { value: 'todos', label: tx('todoHistorico') },
          ],
          ariaLabel: tx('periodoAria'),
        }}
      />

      <div className="px-4 sm:px-6 lg:px-8 space-y-6">
        <p style={{ color: 'var(--muted-ink)', fontSize: 14, fontWeight: 600 }}>
          {tx('experienciaComensales')} <b style={{ color: 'var(--ink)' }}>{currentBranch}</b>
        </p>

        {loadError && (
          <div style={{
            padding: '12px 16px', borderRadius: 'var(--r-md)',
            background: 'rgba(220,38,38,.07)', border: '1.5px solid rgba(220,38,38,.35)',
            color: '#b91c1c', fontSize: 13.5, fontWeight: 700,
          }}>
            ⚠️ {loadError}
          </div>
        )}
        {cargando && (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: 'var(--muted-ink)', fontWeight: 600 }}>
            {tx('cargando')}
          </div>
        )}

        {!cargando && (stats?.global.emitidas ?? 0) === 0 && (
          <div style={{
            textAlign: 'center', padding: '48px 20px', border: '1.5px dashed var(--line-2)',
            borderRadius: 'var(--r-md)', color: 'var(--muted-ink)', fontWeight: 600, fontSize: 14.5,
          }}>
            {tx('sinCalificaciones')}
          </div>
        )}

        {/* ===== Por mesero ===== */}
        {!cargando && (stats?.meseros.length ?? 0) > 0 && (
          <div className="tablecard">
            <div className="tablescroll">
              <table className="cmp">
                <thead>
                  <tr>
                    <th>{tx('colMesero')}</th>
                    <th className="r">{tx('colQrsEmitidos')}</th>
                    <th className="r">{tx('respondidas')}</th>
                    <th className="r">{tx('atencion')}</th>
                    <th className="r">{tx('comida')}</th>
                    <th className="r">{tx('recomendacion')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats!.meseros.map(m => (
                    <tr key={m.mesero}>
                      <td><b>{m.mesero}</b></td>
                      <td className="r tabular-nums">{m.emitidas}</td>
                      <td className="r tabular-nums">{m.respondidas}</td>
                      <td className="r"><Prom v={m.atencion} /></td>
                      <td className="r"><Prom v={m.comida} /></td>
                      <td className="r"><Prom v={m.recomendacion} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ===== Respuestas recientes ===== */}
        {!cargando && respondidas.length > 0 && (
          <div className="space-y-3">
            <h2 style={{ fontSize: 16.5, fontWeight: 800, color: 'var(--ink)' }}>
              {tx('respuestasRecientes', { n: respondidas.length })}
            </h2>
            {respondidas.map(c => (
              <div key={c.id} style={{
                border: '1.5px solid var(--line)', borderRadius: 'var(--r-md)',
                background: 'var(--surface)', padding: 14,
              }}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)' }}>
                      {c.mesero || tx('sinMesero')}{tx('ventaN', { id: c.venta_id })}
                      {c.total != null && <span style={{ color: 'var(--muted-ink)', fontWeight: 700 }}> · {formatMoney(c.total)}</span>}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted-ink)', marginTop: 2 }}>
                      {tx('abiertoCerrado', { abierto: fmtFechaHora(c.abierto_at), cerrado: fmtFechaHora(c.cerrado_at) })}
                      {c.respondida_at && tx('respondidaEl', { fecha: fmtFechaHora(c.respondida_at) })}
                    </div>
                    {c.items_resumen && (
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted-ink)', marginTop: 4 }}>
                        🍽 {c.items_resumen}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1" style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted-ink)' }}>
                    <span>{tx('atencion')} <Stars n={c.q_atencion} /></span>
                    <span>{tx('comida')} <Stars n={c.q_comida} /></span>
                    <span>{tx('recomCorto')} <Stars n={c.q_recomendacion} /></span>
                    {(c.q_velocidad || c.q_limpieza || c.q_ambiente) ? (
                      <span style={{ fontSize: 11 }}>
                        {c.q_velocidad ? tx('velocidadN', { n: c.q_velocidad }) : ''}
                        {c.q_limpieza ? tx('limpiezaN', { n: c.q_limpieza }) : ''}
                        {c.q_ambiente ? tx('ambienteN', { n: c.q_ambiente }) : ''}
                      </span>
                    ) : null}
                  </div>
                </div>
                {c.comentario && (
                  <div className="flex items-start gap-2 mt-2 p-2.5"
                    style={{ background: 'var(--surface-2)', borderRadius: 'var(--r-sm)', fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>
                    <MessageSquareText className="w-4 h-4 shrink-0 mt-0.5" style={{ color: 'var(--muted-ink)' }} />
                    {c.comentario}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
